import React from 'react';
import { Text, View } from 'react-native';
import { CollapsibleSection } from '../collapsible-section/CollapsibleSection';
import { eventDetailsSummaryStyles as styles } from './EventDetailsSummary.styles';

export type CourseScheduleDay = {
  dayLabel: string;
  timeRange: string;
  trainers: string;
};

export type EventDetailsSummaryData = {
  eventDate?: string;
  showTimeRow?: boolean;
  timeRow?: string;
  showTimeslotMeta?: boolean;
  bookingsLine?: string;
  showWaitingListBlock?: boolean;
  waitingListLine?: string;
  waitingPositionLine?: string | null;
  levelLine?: string | null;
  trainersLine?: string | null;
  facilityLine?: string | null;
  showCourseSchedule?: boolean;
  viewScheduleLabel?: string;
  scheduleDays?: CourseScheduleDay[];
  showAttendancePolicy?: boolean;
  attendancePolicyTitle?: string;
  attendancePolicyMessage?: string;
};

type Props = {
  data: EventDetailsSummaryData;
};

function DetailLine({ text }: { text?: string | null }) {
  if (!text) {
    return null;
  }

  return <Text style={styles.mutedText}>{text}</Text>;
}

function EventTimeRow({ data }: { data: EventDetailsSummaryData }) {
  if (!data.showTimeRow) {
    return null;
  }

  return <DetailLine text={data.timeRow} />;
}

function WaitingListDetails({ data }: { data: EventDetailsSummaryData }) {
  if (!data.showWaitingListBlock) {
    return null;
  }

  return (
    <>
      <DetailLine text={data.waitingListLine} />
      <DetailLine text={data.waitingPositionLine} />
    </>
  );
}

function TimeslotMetaDetails({ data }: { data: EventDetailsSummaryData }) {
  if (!data.showTimeslotMeta) {
    return null;
  }

  return (
    <>
      <DetailLine text={data.bookingsLine} />
      <WaitingListDetails data={data} />
      <DetailLine text={data.levelLine} />
      <DetailLine text={data.trainersLine} />
      <DetailLine text={data.facilityLine} />
    </>
  );
}

function AttendancePolicyDetails({ data }: { data: EventDetailsSummaryData }) {
  if (!data.showAttendancePolicy) {
    return null;
  }

  return (
    <View style={styles.attendanceSection}>
      <Text style={styles.mutedText}>{data.attendancePolicyTitle}</Text>
      <Text style={styles.mutedText}>{data.attendancePolicyMessage}</Text>
    </View>
  );
}

function formatScheduleDayRest(day: CourseScheduleDay): string {
  const rest = day.trainers ? `${day.timeRange}, ${day.trainers}` : day.timeRange;
  return ` ${rest}`;
}

function CourseScheduleDayRow({ day }: { day: CourseScheduleDay }) {
  return (
    <View style={styles.scheduleDay}>
      <Text style={styles.mutedText}>
        <Text style={styles.scheduleDayLabel}>{day.dayLabel}</Text>
        {formatScheduleDayRest(day)}
      </Text>
    </View>
  );
}

function CourseScheduleBlock({
  data,
  scheduleDays,
}: {
  data: EventDetailsSummaryData;
  scheduleDays: CourseScheduleDay[];
}) {
  return (
    <View style={styles.scheduleSection}>
      <CollapsibleSection
        data={{
          label: data.viewScheduleLabel || 'Weekly schedule',
        }}
      >
        <View style={styles.scheduleList}>
          {scheduleDays.map((day, index) => (
            <CourseScheduleDayRow key={`${day.dayLabel}-${index}`} day={day} />
          ))}
        </View>
      </CollapsibleSection>
    </View>
  );
}

function CourseScheduleSection({ data }: Props) {
  const scheduleDays = data.scheduleDays ?? [];

  if (!data.showCourseSchedule || scheduleDays.length === 0) {
    return null;
  }

  return <CourseScheduleBlock data={data} scheduleDays={scheduleDays} />;
}

export function EventDetailsSummary({ data }: Props) {
  if (!data) {
    return null;
  }

  return (
    <View style={styles.wrapper}>
      <CourseScheduleSection data={data} />

      <View>
        <DetailLine text={data.eventDate} />
        <EventTimeRow data={data} />
        <TimeslotMetaDetails data={data} />
      </View>

      <AttendancePolicyDetails data={data} />
    </View>
  );
}
